import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { AdminEditorService } from '../admin.service';
import { AdminSectionEditorAction } from '../editor/section/section.component';
import { SafeHtml } from '@angular/platform-browser';
import { ContentHeaderAction } from 'app/key-ui/content-header/content-header.component';
import { Observable } from 'rxjs';

export interface EntityListResult {
    title: string;
    type: string;
    count: number;
    limit: number;
    offset: number;
    items: any[];
}

export interface SectionHandlerGroup<T> {
    name: string;
    sections: SectionHandler<T, any>[];
    message?: string;
    warnings?: SafeHtml[];
    actions?: ContentHeaderAction[];
}

export type ButtonTypes = 'ok' | 'save' | 'cancel' | string;

export interface ActionTriggeredResult {
    updatePayload?: any; // return an update payload to trigger an applyUpdate after the action is triggered
}
export interface SectionHandler<TEntity, TValues> {

    type: string;

    icon: string;
    title: string;
    description: string;
    bulk: boolean;
    hidden?: boolean;
    warning?: string;

    readonly?: boolean;

    actions?: AdminSectionEditorAction[];

    buttons?: ButtonTypes[]; // default is ['save', 'cancel']

    /** Return an object with only the fields that this section needs to feed to its editor,
     *  this does not have to map directly to the entity, as it can be mapped back in updateEntity()
     */
    mapEntityToEditor(entity: TEntity): Promise<TValues>;

    /** Take the values from the editor and apply them to an entity object */
    mapEditorToEntity(entity: any, values: TValues): Promise<any>;

    applyUpdate(entity: TEntity, payload: any): Promise<TEntity>;

    actionTriggered?(entity: TEntity, action: string): Promise<ActionTriggeredResult | void>;

}

export interface FormSectionHandler<TEntity, TValues> extends SectionHandler<TEntity, TValues> {

    getFields(entity: TEntity): Promise<FormBuilderField[]>;
    mapValidationErrors?(errors: { [field: string]: string }): { [field: string]: string };
    onIconClicked?(entity: TEntity): Promise<TEntity>;

}


export interface EditorHandler {


    listMode: 'grid' | 'tree';

    states: string[];

    actions: { icon: string, name: string, click: (entity: any) => void }[];
    bulkActions: { icon: string, name: string, click: (entities: any[]) => void }[];
    gridActions: { icon: string, name: string, click: (ownerId: string) => void }[];
    changesVisible: boolean;
    filterEnabled?: boolean;

    /** when sorted by any of these columns, the items in the grid will be grouped */
    groupColumns?: string[];

    getEntity(id: string): Promise<any> | Observable<any>;
    getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<any>[]>;

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult>;

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]>;
    setEntityState(entity: any, state: string): Promise<any>;

    createEntity(ownerId: string, entity: any): Promise<any>;

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<any, any>[] }>;



}


/** The base class on which all entity handlers are based. Contains abstract method definitions and a number of utility methods
 *  that can be used by descendant handlers.  */
@Injectable()
export abstract class EntityHandler<T> implements EditorHandler {

    listMode: 'grid' | 'tree' = 'grid';

    states: string[] = [];

    actions: { icon: string, name: string, click: (entity: T) => void }[] = [];
    bulkActions = [];

    gridActions: { icon: string, name: string, click: (ownerId: string) => void }[] = [];
    changesVisible = true;
    filterEnabled = true;

    constructor(
        public admin: AdminEditorService
    ) {

    }

    abstract getEntity(id: string): Promise<T> | Observable<T>;
    abstract getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<T>[]>;

    listEntities(_ownerId: string, _offset: number, _limit: number, _sort: string, _filter: string): Promise<EntityListResult> {
        return Promise.reject(new Error('Not implemented'));
    }

    getGridColumns(_ownerId: string): Promise<IKuiTableCol[]> {
        return Promise.reject(new Error('Not implemented'));
    }

    setEntityState(_entity: T, _state: string): Promise<T> {
        return Promise.reject(new Error('Not implemented'));
    }

    createEntity(_ownerId: string, _entity: any): Promise<any> {
        return Promise.reject(new Error('Not implemented'));
    }

    getCreationSections(_ownerId: string): Promise<{ entityType: string, entity: any, sections: FormSectionHandler<T, any>[] }> {
        return Promise.reject(new Error('Not implemented'));
    }

}


